import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Filter, Loader2, Receipt } from "lucide-react";
import { API_BASE_URL } from "../../../config";
import { TeamLogo } from "../bondhouse/FixtureStatus";
import { fmtFull } from "../../utils/betDetailHelpers";
import {
  TICKET_STATUS_FILTERS,
  fetchHouseTickets,
  filterHouseTickets,
  fmtTicketCash,
  getTicketPulse,
  getTicketStatusCounts,
  ticketStatusTone,
} from "../../utils/houseTickets";

const FILTER_LABELS = {
  all: "All tickets",
  pending: "Open",
  won: "Won",
  paid: "Paid",
  lost: "Lost",
  voided: "Voided",
  refunded: "Refunded",
};

const TicketRow = ({ ticket, index }) => {
  const [open, setOpen] = useState(false);
  const tone = ticketStatusTone(ticket.status);

  return (
    <article className={`bd-ticket-row bd-ticket-row-${tone} ${open ? "bd-ticket-row-open" : ""}`}>
      <button
        type="button"
        className="bd-ticket-row-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="bd-ticket-row-seq tabular-nums">#{index + 1}</span>
        <span className="bd-ticket-row-main">
          <span className="bd-ticket-row-top">
            <span className="bd-ticket-row-stake tabular-nums">{fmtTicketCash(ticket.amount)}</span>
            <span className={`bd-ticket-status bd-ticket-status-${tone}`}>{ticket.status}</span>
          </span>
          <span className="bd-ticket-row-meta">
            <span>{ticket.picks} pick{ticket.picks === 1 ? "" : "s"}</span>
            <span aria-hidden>·</span>
            <span className="tabular-nums">{ticket.odds.toFixed(2)}x</span>
            {ticket.when ? (
              <>
                <span aria-hidden>·</span>
                <span>{ticket.when}</span>
              </>
            ) : null}
          </span>
        </span>
        <ChevronDown size={14} className="bd-ticket-row-chevron" aria-hidden />
      </button>

      {open ? (
        <div className="bd-ticket-detail">
          <div className="bd-ticket-detail-metrics">
            <div className="bd-ticket-metric">
              <span className="bd-ticket-metric-label">Picks</span>
              <span className="bd-ticket-metric-value tabular-nums">{ticket.picks}</span>
            </div>
            <div className="bd-ticket-metric">
              <span className="bd-ticket-metric-label">Combined odds</span>
              <span className="bd-ticket-metric-value tabular-nums">{ticket.odds.toFixed(2)}x</span>
            </div>
            {ticket.potential > 0 ? (
              <div className="bd-ticket-metric bd-ticket-metric-highlight">
                <span className="bd-ticket-metric-label">Potential return</span>
                <span className="bd-ticket-metric-value tabular-nums">
                  {fmtTicketCash(ticket.potential)}
                </span>
              </div>
            ) : null}
            {ticket.when ? (
              <div className="bd-ticket-metric">
                <span className="bd-ticket-metric-label">Placed</span>
                <span className="bd-ticket-metric-value">{ticket.when}</span>
              </div>
            ) : null}
          </div>

          {ticket.legs.length > 0 ? (
            <>
              <p className="bd-ticket-legs-title">Selections on this ticket</p>
              <ul className="bd-ticket-legs">
                {ticket.legs.map((leg, legIdx) => (
                  <li key={`${ticket.id}-leg-${legIdx}`} className="bd-ticket-leg-card">
                    <div className="bd-ticket-leg-match">
                      <div className="bd-ticket-leg-team">
                        <TeamLogo team={leg.homeTeam || { name: leg.home }} size="sm" />
                        <span className="truncate">{leg.home}</span>
                      </div>
                      <span
                        className={`bd-ticket-leg-status ${
                          leg.isLive
                            ? "bd-ticket-leg-status-live"
                            : leg.isDone
                            ? "bd-ticket-leg-status-done"
                            : ""
                        }`}
                      >
                        {leg.fixtureStatusLabel}
                      </span>
                      <div className="bd-ticket-leg-team bd-ticket-leg-team-away">
                        <span className="truncate">{leg.away}</span>
                        <TeamLogo team={leg.awayTeam || { name: leg.away }} size="sm" />
                      </div>
                    </div>
                    <div className="bd-ticket-leg-foot">
                      <span className="bd-ticket-leg-pick">{leg.pick}</span>
                      {leg.odds > 1 ? (
                        <span className="bd-ticket-leg-odds tabular-nums">{leg.odds.toFixed(2)}x</span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

const HouseTicketsRail = ({ houseId, canManage, seedCount = 0 }) => {
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTickets = useCallback(async () => {
    if (!houseId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchHouseTickets(houseId, API_BASE_URL);
      setTickets(rows);
    } catch (err) {
      setError(err.message || "Could not load tickets");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [houseId]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const pulse = useMemo(() => getTicketPulse(tickets), [tickets]);
  const statusCounts = useMemo(() => getTicketStatusCounts(tickets), [tickets]);
  const filtered = useMemo(
    () => filterHouseTickets(tickets, statusFilter),
    [tickets, statusFilter]
  );

  const displayTotal = loading && !tickets.length ? seedCount : pulse.total;

  return (
    <aside className="bd-tickets-rail-inner" aria-label="House ticket filters">
      <header className="bd-tickets-rail-head">
        <p className="bd-tickets-rail-eyebrow">Ticket lane</p>
        <h2 className="bd-tickets-rail-title tabular-nums">{fmtFull(displayTotal)} tickets</h2>
        <div className="bd-tickets-rail-stats">
          <span>
            <strong className="tabular-nums">{fmtFull(pulse.open)}</strong> open
          </span>
          <span className="bd-tickets-rail-stat-dot" aria-hidden />
          <span className="truncate">{fmtTicketCash(pulse.volume)} vol</span>
        </div>
      </header>

      <div className="bd-tickets-rail-body">
        <div className="bd-tickets-rail-section">
          <p className="bd-tickets-rail-label">
            <Filter size={11} aria-hidden />
            Filter deck
          </p>
          <div className="bd-tickets-filter-grid">
            {TICKET_STATUS_FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={`bd-tickets-filter-chip ${
                  statusFilter === key ? "bd-tickets-filter-chip-active" : ""
                }`}
                onClick={() => setStatusFilter(key)}
              >
                <span className="bd-tickets-filter-chip-copy">
                  <span>{FILTER_LABELS[key] || key}</span>
                </span>
                <strong className="tabular-nums">{statusCounts[key] ?? 0}</strong>
              </button>
            ))}
          </div>
          {statusFilter !== "all" ? (
            <p className="bd-tickets-filter-hint">
              Showing <strong className="tabular-nums">{filtered.length}</strong> ticket
              {filtered.length === 1 ? "" : "s"}
            </p>
          ) : null}
        </div>

        <div className="bd-tickets-rail-list-wrap">
          {loading ? (
            <div className="bd-tickets-state">
              <Loader2 size={16} className="animate-spin" />
              <span>Loading</span>
            </div>
          ) : error ? (
            <div className="bd-tickets-state bd-tickets-state-error">
              <p>{error}</p>
              <button type="button" className="bd-tickets-retry" onClick={loadTickets}>
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bd-tickets-state">
              <Receipt size={18} strokeWidth={1.8} />
              <p>No tickets in this lane yet.</p>
            </div>
          ) : (
            <div className="bd-tickets-list">
              {filtered.map((ticket, index) => (
                <TicketRow key={ticket.id} ticket={ticket} index={index} />
              ))}
            </div>
          )}
        </div>

        {canManage ? (
          <Link to={`/app/bethouse/${houseId}/manage`} className="bd-tickets-manage-link">
            Operator ticket desk
          </Link>
        ) : null}
      </div>
    </aside>
  );
};

export default HouseTicketsRail;
